import { Badge, Box, Button, Card, Group, List, SimpleGrid, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import {
  IconArrowRight,
  IconBooks,
  IconBrain,
  IconChartLine,
  IconCpu,
  IconExternalLink,
  IconFunction,
  IconHierarchy3,
  IconMessageChatbot,
  IconShieldCheck,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { Eyebrow, StatCard } from "../theme/components";
import { CodeBlock } from "../components/JsonView";
import { easyAiOrigin } from "../lib/clients";

const FEATURES = [
  {
    icon: IconFunction,
    title: "AI functions",
    body: "Describe the task in plain language, define the JSON you want back, and get a hosted, versioned API endpoint. No servers, no prompt plumbing.",
  },
  {
    icon: IconHierarchy3,
    title: "Chains",
    body: "Connect functions, searches, Passio API calls and code steps into one multi-step flow, called as a single endpoint. The meal plans in this demo are Easy AI chains.",
    link: { to: "/meal-plans", label: "See a chain run" },
  },
  {
    icon: IconBooks,
    title: "Knowledge bases",
    body: "Upload menus, product catalogs, clinical guidelines or brand rules. Functions retrieve from them, so answers come from your data rather than the model's memory.",
  },
  {
    icon: IconShieldCheck,
    title: "Reliability built in",
    body: "Fallback models, retries and time limits per function, plus live model health checks, so one provider having a bad day doesn't take your feature down.",
  },
  {
    icon: IconBrain,
    title: "Self-learning",
    body: "Turn on learning and Studio reviews recent runs, builds a knowledge base of what worked and what failed, and suggests prompt or model changes for you to approve. Feedback makes it sharper.",
  },
  {
    icon: IconCpu,
    title: "Any model",
    body: "The same catalog as the Models page, switchable per function, including zero-data-retention models for regulated data.",
    link: { to: "/models", label: "Compare models" },
  },
  {
    icon: IconChartLine,
    title: "See every run",
    body: "Run history, request lookup, cost per run, performance insights and alerts when latency or error rates drift.",
  },
  {
    icon: IconMessageChatbot,
    title: "Playground and chatbots",
    body: "Try prompts on several models side by side before you ship, and embed a chatbot built on your functions and knowledge.",
  },
];

const IDEAS = [
  "Check a restaurant menu against a user's allergies and diet, using your own menu data",
  "Score meals with your program's own points or rules, from a Passio photo scan",
  "Write coaching replies in your brand's voice, grounded in your guidelines",
  "Turn a grocery receipt into pantry items and a shopping list",
  "Rewrite or localize recipes for a region, a budget or a cooking skill level",
  "Flag label or claim issues on packaged products before they go live",
];

const chainSnippet = (origin: string) => `import { EasyAIClient } from "@passio/easyai-client";

const easyai = new EasyAIClient({ baseUrl: "${origin}", apiKey: process.env.EASYAI_KEY });

// Run a chain you built in Studio, with Passio data from an earlier call as input.
const { output, execution } = await easyai.run(CHAIN_ID, { foods: scan.data, goal: "low sodium" });

// Tell it how the result went: the learning loop uses this.
await easyai.sendFeedback([{ source_type: "chain_execution", source_id: execution.id, label: "accepted" }]);`;

/** "Don't see what you need?": what Easy AI Studio adds on top of the NutritionAI routes. */
export function StudioPage() {
  const origin = easyAiOrigin();
  return (
    <Stack gap={40}>
      <Box className="pd-dark" p={{ base: 24, sm: 40 }} style={{ borderRadius: 20 }}>
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="xl">
          <Stack gap="md" maw={660}>
            <Eyebrow>Easy AI Studio</Eyebrow>
            <Title order={1} fz={{ base: 32, sm: 44 }} lh={1.08} c="white">
              Don't see what you need? Build it.
            </Title>
            <Text className="pd-dim" fz="lg">
              The NutritionAI routes cover the common jobs. For everything specific to your product, Easy AI Studio lets you build and host your own AI functions, chains and knowledge bases, and call them from your app the same way.
            </Text>
            <Group>
              <Button component="a" href={`${origin}/dashboard/playground?platform=nutrition&new=1`} target="_blank" rel="noreferrer" size="md" color="lime" c="ink.8" rightSection={<IconExternalLink size={16} />}>
                Build a NutritionAI app
              </Button>
              <Button component="a" href={`${origin}/dashboard`} target="_blank" rel="noreferrer" size="md" variant="outline" color="gray.0">
                Open Easy AI Studio
              </Button>
            </Group>
            <Text className="pd-dim" fz="xs">
              Sign in with your Passio account. The app builder opens with NutritionAI set up: describe the app, preview it live, and wire in your own functions and chains.
            </Text>
          </Stack>
          <Stack gap="sm" w={{ base: "100%", sm: 300 }}>
            <StatCard accent caption="included with NutritionAI" title="$10 of Studio usage, free every month">
              <Text fz="sm" c="var(--pd-ink-muted)" mt={6}>
                Enough to prototype, test on real data and run a feature in production at low volume before you pay anything extra.
              </Text>
            </StatCard>
          </Stack>
        </Group>
      </Box>

      <Box>
        <Title order={2} fz={24} mb={4}>
          What you get
        </Title>
        <Text c="dimmed" fz="sm" mb="md">
          The same platform the NutritionAI routes run on.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          {FEATURES.map((f) => (
            <Card key={f.title} className="pd-tile">
              <Stack gap={8} h="100%">
                <ThemeIcon size={36} radius="md" color="ink.8" c="lime.5">
                  <f.icon size={20} />
                </ThemeIcon>
                <Text fw={700}>{f.title}</Text>
                <Text fz="sm" c="dimmed">
                  {f.body}
                </Text>
                {f.link && (
                  <Button component={Link} to={f.link.to} variant="subtle" size="compact-sm" mt="auto" w="fit-content" px={0} rightSection={<IconArrowRight size={14} />}>
                    {f.link.label}
                  </Button>
                )}
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Box>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <Card>
          <Group gap="xs" mb="sm">
            <Text fw={700}>Things teams build</Text>
            <Badge variant="light" color="violet">
              ideas
            </Badge>
          </Group>
          <List spacing={8} fz="sm" c="dimmed">
            {IDEAS.map((i) => (
              <List.Item key={i}>{i}</List.Item>
            ))}
          </List>
        </Card>
        <Card>
          <Text fw={700} mb={4}>
            Call it next to your Passio calls
          </Text>
          <Text fz="sm" c="dimmed" mb="sm">
            Build in Studio, then call it from your backend with an Easy AI API key. The same typed client this demo uses is on the Client SDKs page.
          </Text>
          <CodeBlock code={chainSnippet(origin)} language="ts" maxHeight={300} />
          <Button component={Link} to="/developers" variant="subtle" size="compact-sm" mt="xs" px={0} rightSection={<IconArrowRight size={14} />}>
            Client SDKs
          </Button>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
